import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";

import {
  FormRow,
  FormSection,
} from "../../../../../components/layout/forms/Index";

import TextField from "../../../../../components/input/textfield/TextField";
import TextArea from "../../../../../components/input/textarea/TextArea";
import Select from "../../../../../components/input/select/Select";

import { compressImageFile } from "../../../../../utils/helpers/compression/imageCompressor";
import { uploadThumbnailToS3 } from "../../../../../utils/helpers/s3Uploader/uploadThumbnailToS3";

import * as StreamService from "../../../creators/services/creatorStreamsService";

import "./CreatorEditStreamForm.css";

const STREAM_TYPE_OPTIONS = [
  {
    value: "match",
    label: "Match",
  },
  {
    value: "training",
    label: "Training",
  },
  {
    value: "event",
    label: "Event",
  },
];

const ACCESS_TYPE_OPTIONS = [
  {
    value: "public",
    label: "Public",
  },
  {
    value: "unlisted",
    label: "Unlisted",
  },
  {
    value: "group",
    label: "Group",
  },
  {
    value: "private",
    label: "Private",
  },
];

const createInitialFormData = () => ({
  title: "",
  description: "",
  streamType: "match",
  accessType: "public",
  scheduledStart: "",
  thumbnailUrl: "",
  thumbnailFile: null,
  thumbnailPreview: "",
  recordingEnabled: false,
});

const formatDateTimeLocal = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;

  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const CreatorEditStreamForm = ({
  stream,
  loading = false,
  error,
  onSave,
  onClose,
  onCancel,
}) => {
  const [formData, setFormData] = useState(createInitialFormData);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailProgress, setThumbnailProgress] = useState(0);

  const thumbnailPreviewRef = useRef("");

  const closeForm = onClose || onCancel;

  const isScheduled =
    String(stream?.status || "").toLowerCase() === "scheduled";

  const isBusy = loading || saving || thumbnailUploading;

  const currentThumbnail =
    formData.thumbnailPreview || formData.thumbnailUrl || "";

  const isSubmitDisabled = isBusy || !stream || !formData.title.trim();

  const errorMessage =
    formError ||
    (typeof error === "string" ? error : getErrorMessage(error, ""));

  useEffect(() => {
    if (thumbnailPreviewRef.current) {
      URL.revokeObjectURL(thumbnailPreviewRef.current);
      thumbnailPreviewRef.current = "";
    }

    if (!stream) {
      setFormData(createInitialFormData());

      return;
    }

    setFormData({
      title: stream.title || "",
      description: stream.description || "",
      streamType: stream.streamType || stream.stream_type || "match",
      accessType: stream.accessType || stream.access_type || "public",
      scheduledStart: formatDateTimeLocal(
        stream.scheduledStart || stream.scheduled_start,
      ),
      thumbnailUrl: stream.thumbnailUrl || stream.thumbnail_url || "",
      thumbnailFile: null,
      thumbnailPreview: "",
      recordingEnabled:
        Number(stream.recordingEnabled ?? stream.recording_enabled) === 1,
    });

    setFormError("");
    setThumbnailProgress(0);
  }, [stream]);

  useEffect(() => {
    thumbnailPreviewRef.current = formData.thumbnailPreview;
  }, [formData.thumbnailPreview]);

  useEffect(() => {
    return () => {
      if (thumbnailPreviewRef.current) {
        URL.revokeObjectURL(thumbnailPreviewRef.current);
      }
    };
  }, []);

  const clearFormError = () => {
    if (formError) {
      setFormError("");
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    clearFormError();

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleThumbnailChange = (event) => {
    const file = event.target.files?.[0] || null;

    event.target.value = "";

    clearFormError();

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setFormError("Thumbnail must be an image.");

      return;
    }

    const preview = URL.createObjectURL(file);

    setFormData((current) => {
      if (current.thumbnailPreview) {
        URL.revokeObjectURL(current.thumbnailPreview);
      }

      return {
        ...current,
        thumbnailFile: file,
        thumbnailPreview: preview,
      };
    });
  };

  const handleRemoveThumbnail = () => {
    if (isBusy) {
      return;
    }

    setFormData((current) => {
      if (current.thumbnailPreview) {
        URL.revokeObjectURL(current.thumbnailPreview);
      }

      return {
        ...current,
        thumbnailUrl: "",
        thumbnailFile: null,
        thumbnailPreview: "",
      };
    });

    setThumbnailProgress(0);
    setFormError("");
  };

  const uploadThumbnail = async () => {
    if (!formData.thumbnailFile) {
      return formData.thumbnailUrl || null;
    }

    setThumbnailUploading(true);
    setThumbnailProgress(0);

    const compressedFile = await compressImageFile({
      file: formData.thumbnailFile,
      maxWidth: 1280,
      maxHeight: 720,
      quality: 0.72,
      outputType: "image/jpeg",
    });

    const response = await StreamService.createStreamThumbnailUploadUrl(
      compressedFile.type,
    );

    const { uploadUrl, thumbnailUrl } = response?.data || {};

    if (!uploadUrl || !thumbnailUrl) {
      throw new Error("Unable to create the thumbnail upload URL.");
    }

    await uploadThumbnailToS3({
      uploadUrl,
      file: compressedFile,
      onProgress: setThumbnailProgress,
    });

    setThumbnailProgress(100);

    return thumbnailUrl;
  };

  const handleClose = () => {
    if (isBusy) {
      return;
    }

    closeForm?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isBusy) {
      return;
    }

    const title = formData.title.trim();
    const description = formData.description.trim();

    if (!stream) {
      setFormError("Stream information is missing.");

      return;
    }

    if (!title) {
      setFormError("Stream title is required.");

      return;
    }

    try {
      setSaving(true);
      setFormError("");

      const thumbnailUrl = await uploadThumbnail();

      const payload = {
        title,
        description,
        streamType: formData.streamType,
        accessType: formData.accessType,
        scheduledStart: formData.scheduledStart || null,
        thumbnailUrl,
      };

      if (isScheduled) {
        payload.recordingEnabled = formData.recordingEnabled ? 1 : 0;
      }

      const result = await onSave?.(payload);

      if (result === false || result?.success === false) {
        setFormError(result?.message || "Failed to update stream details.");

        return;
      }

      closeForm?.();
    } catch (submitError) {
      setFormError(
        getErrorMessage(submitError, "Failed to update stream details."),
      );
    } finally {
      setThumbnailUploading(false);
      setSaving(false);
    }
  };

  const progress = Math.min(
    100,
    Math.max(0, Math.round(Number(thumbnailProgress) || 0)),
  );

  const submitLabel = thumbnailUploading
    ? `Uploading ${progress}%`
    : saving || loading
      ? "Saving..."
      : "Save Details";

  return (
    <form
      className="creator-edit-stream-form"
      onSubmit={handleSubmit}
      aria-busy={isBusy}
    >
      {errorMessage && (
        <div className="creator-edit-stream-form__error" role="alert">
          {errorMessage}
        </div>
      )}

      <FormSection className="creator-edit-stream-form__fields">
        <TextField
          label="Stream Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="Bucks vs Harlequins"
          maxLength={120}
          required
          disabled={isBusy}
        />

        <TextArea
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Add information about the stream"
          maxLength={1000}
          maxHeight={120}
          disabled={isBusy}
        />

        <FormRow>
          <Select
            label="Stream Type"
            name="streamType"
            value={formData.streamType}
            onChange={handleInputChange}
            items={STREAM_TYPE_OPTIONS}
            required
            disabled={isBusy}
          />

          <Select
            label="Access Type"
            name="accessType"
            value={formData.accessType}
            onChange={handleInputChange}
            items={ACCESS_TYPE_OPTIONS}
            required
            disabled={isBusy}
          />
        </FormRow>

        {formData.accessType === "group" && (
          <div className="creator-edit-stream-form__note">
            Group access is managed in Shared Groups. Add at least one group to
            provide group access.
          </div>
        )}

        <TextField
          label="Scheduled Start"
          type="datetime-local"
          name="scheduledStart"
          value={formData.scheduledStart}
          onChange={handleInputChange}
          disabled={isBusy}
        />

        {isScheduled ? (
          <label className="creator-edit-stream-form__checkbox">
            <input
              type="checkbox"
              name="recordingEnabled"
              checked={formData.recordingEnabled}
              onChange={handleInputChange}
              disabled={isBusy}
            />

            <span>Enable recording after the live stream</span>
          </label>
        ) : (
          <div className="creator-edit-stream-form__note">
            Recording settings are locked after the stream starts.
          </div>
        )}
      </FormSection>

      <section className="creator-edit-stream-form__thumbnail">
        <div className="creator-edit-stream-form__thumbnail-row">
          <div className="creator-edit-stream-form__thumbnail-heading">
            <h3>Thumbnail</h3>
            <p>Optional · Recommended 1280 × 720</p>
          </div>

          <div className="creator-edit-stream-form__thumbnail-control">
            {currentThumbnail && (
              <div className="creator-edit-stream-form__preview">
                <img src={currentThumbnail} alt="Stream thumbnail preview" />

                <button
                  type="button"
                  className="creator-edit-stream-form__remove-thumbnail"
                  onClick={handleRemoveThumbnail}
                  disabled={isBusy}
                  aria-label="Remove thumbnail"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            <label className="creator-edit-stream-form__thumbnail-picker">
              <ImagePlus size={17} />

              <span>{currentThumbnail ? "Replace" : "Add thumbnail"}</span>

              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleThumbnailChange}
                disabled={isBusy}
                hidden
              />
            </label>
          </div>
        </div>

        {thumbnailUploading && (
          <div className="creator-edit-stream-form__progress">
            <div className="creator-edit-stream-form__progress-heading">
              <span>Uploading thumbnail</span>
              <strong>{progress}%</strong>
            </div>

            <div className="creator-edit-stream-form__progress-track">
              <div
                className="creator-edit-stream-form__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </section>

      <div className="creator-edit-stream-form__actions">
        <button
          type="button"
          className="creator-edit-stream-form__cancel"
          onClick={handleClose}
          disabled={isBusy}
        >
          Cancel
        </button>

        <button
          type="submit"
          className="creator-edit-stream-form__submit"
          disabled={isSubmitDisabled}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default CreatorEditStreamForm;
